import { useState, useEffect, useCallback } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import Button from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

interface OutletContext {
  currentStep: number
  goToStep: (index: number) => void
  handleForward: () => void
  handleBack: () => void
}

export default function ManifestoScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, refreshProfile } = useAuth()
  const { handleForward } = useOutletContext<OutletContext>()

  const paragraphs = [
    t('onboarding.manifesto_p1'),
    t('onboarding.manifesto_p2'),
    t('onboarding.manifesto_p3'),
  ]
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  const handleContinue = useCallback(() => {
    handleForward()
  }, [handleForward])

  // Skip the whole onboarding: mark as onboarded (same write as DoneScreen)
  // and go to the same destination as the final screen. Never blocks —
  // on any error we still navigate away.
  const handleSkipAll = useCallback(async () => {
    try {
      if (user?.id) {
        await supabase
          .from('profiles')
          .update({ onboarded_at: new Date().toISOString() })
          .eq('id', user.id)
          .is('onboarded_at', null)
        await refreshProfile()
      }
    } catch (e) {
      console.error('Could not skip onboarding:', e)
    } finally {
      navigate('/home')
    }
  }, [user?.id, refreshProfile, navigate])

  return (
    <div className="flex flex-col min-h-[calc(100vh-180px)] py-8">
      {/* Decorative quotation mark */}
      <div
        className={`text-green font-display text-[80px] leading-none select-none transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        aria-hidden="true"
      >
        {"\u201C"}
      </div>

      {/* Manifesto text */}
      <div className="flex-1 space-y-6 -mt-4">
        {paragraphs.map((text, i) => (
          <p
            key={i}
            className={`font-display text-lg leading-[1.7] text-ink transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: `${150 + i * 120}ms` }}
          >
            {text}
          </p>
        ))}

        {/* The italic question */}
        <p
          className={`font-display text-xl leading-[1.7] text-ink italic transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: `${150 + paragraphs.length * 120}ms` }}
        >
        `“The function of the artist is to make people like life better than they have before.”
— Kurt Vonnegut
        </p>

        {/* Closing decorative mark */}
        <p
          className={`text-green font-display text-[40px] leading-none select-none transition-all duration-700 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            transitionDelay: `${250 + paragraphs.length * 120}ms`,
          }}
          aria-hidden="true"
        >
          {"\u201D"}
        </p>
      </div>

      {/* Continue button */}
      <div
        className={`pt-8 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: `${350 + paragraphs.length * 120}ms` }}
      >
        <Button onClick={handleContinue} className="w-full h-12 btn-primary">
          {t('onboarding.join')}
        </Button>
        {/* Escape hatch for already-registered users */}
        <div className="pt-4 text-center">
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm text-ink-muted hover:text-ink transition-colors"
          >
            {t('onboarding.haveAccount')}
          </button>
        </div>
        {/* Skip the entire onboarding — quiet escape hatch */}
        <div className="pt-2 text-center">
          <button
            onClick={handleSkipAll}
            className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink transition-colors"
          >
            {t('onboarding.skip_all')}
          </button>
        </div>
      </div>
    </div>
  )
}
